import {AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';

import {pluralize} from 'common/util/string';
import {stickyElemHooks} from 'common/util/dom-sticky-stack';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  CellClickEvent,
  ColumnMovedEvent,
  NumResultsChangeEvent,
  RowClickEvent,
  RowDetailsVisibilityChangeEvent,
  SelectionChangeEvent,
  SortChangeEvent,
  TableRow,
} from 'common/components/table/types';
import {FilterBarJsxProps} from 'common/components/filter-bar';
import {
  ApplyFilterEvent,
  FilterMenuChangeEvent,
  FilterSelectAllEvent,
  GetFilterSortedMenuSections,
  UpdateInputEvent,
} from 'common/components/filter-bar/types';
import {SelectionState} from 'common/components/items-menu/constants';

import {
  ActionOption,
  FilterBarState,
  FilterBarTableColumn,
  FilterBarTableRow,
  FilterBarTableState,
  FilterResultsLabelFormatter,
  FilterSelectConfig,
  FilterState,
  FilterStateChangeEvent,
  ParsedFilterSelectConfig,
  ParsedFilterSimpleConfig,
  SearchChangeEvent,
  SelectChangeEvent,
  TabChangeEvent,
  TableAttrs,
  ToggleChangeEvent,
  UpdatedParseFilterSelectionConfigsEvent,
} from './types';
import {template} from './template';
import css from './index.inline.styl';
import {track, TrackingEvent, TrackingEventProperty} from './tracking';

const SEARCH_INPUT_TIME_MS = 500;

interface FilterTableJsxProps {
  attrs?: {
    elref?: string;
    'total-results'?: number;
    'hide-filter-bar'?: boolean;
    'hide-header'?: boolean;
    'scroll-top-on-select'?: boolean;
    'sticky-titles'?: boolean;
    'tab-theme'?: string;
    'track-name'?: string;
    'search-debouncing'?: boolean;
    'render-empty-state-slot'?: boolean;
  };
  props: {
    actionOptions?: ActionOption[];
    barAttrs?: FilterBarJsxProps['attrs'];
    columns?: FilterBarTableColumn[];
    customHeaderBarRenderer?: () => VNode;
    filterBarSelectActionRenderer?: (c) => VNode;
    filterResultsLabelFormatter?: FilterResultsLabelFormatter;
    filterSelectConfigs?: FilterSelectConfig[];
    filterState?: FilterState;
    filterSearchPlaceholder?: string;
    getFilterSortedMenuSections?: GetFilterSortedMenuSections;
    rowDetailsRenderer?: (row: TableRow) => VNode;
    rows?: FilterBarTableRow[];
    selectedTab?: string;
    tableAttrs: TableAttrs;
    tabs?: Array<{value: string; label: string}>;
  };
  on?: {
    // filter-bar-table actions
    tabChange?: (ev: TabChangeEvent) => void;
    filterStateChange?: (ev: FilterStateChangeEvent) => void;
    updateParsedFilterSelectConfigs?: (ev: UpdatedParseFilterSelectionConfigsEvent) => void;
    searchStarted?: () => void;

    // table actions
    selectionChange?: (ev: SelectionChangeEvent) => void;
    rowClick?: (ev: RowClickEvent<TableRow>) => void;
    cellClick?: (ev: CellClickEvent<TableRow>) => void;
    sortChange?: (ev: SortChangeEvent<TableRow>) => void;
    numResultsChange?: (ev: NumResultsChangeEvent) => void;
    rowDetailsVisibilityChange?: (ev: RowDetailsVisibilityChangeEvent) => void;
    columnMoved?: (ev: ColumnMovedEvent) => void;

    // filter-bar actions
    clickAction?: (ev: SelectChangeEvent) => void;
    clearSelected?: () => void;
    changeFilter?: (ev: SelectChangeEvent) => void;
    applyFilter?: (ev: ApplyFilterEvent) => void;
    filterMenuChange?: (ev: FilterMenuChangeEvent) => void;
    filterSelectAll?: (ev: FilterSelectAllEvent) => void;
    updateInput?: (ev: UpdateInputEvent) => void;
  };
}

export class FilterBarTable extends Component<FilterBarTableState, FilterTableJsxProps['attrs']> {
  searchChangedExecutor = null;
  static get attrsSchema(): AttrsSchema<FilterTableJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      elref: `string`,
      'total-results': `number`,
      'hide-filter-bar': `boolean`,
      'hide-header': `boolean`,
      'scroll-top-on-select': `boolean`,
      'sticky-titles': `boolean`,
      // The theme of the mp-toggle component
      'tab-theme': `string`,
      'track-name': {
        type: `string`,
        description: `If it is set, filter-bar-table tracks search event with an extra 'name' property equal to this value`,
        default: null,
      },
      'search-debouncing': `boolean`,
      'render-empty-state-slot': `boolean`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        tabs: [],
        selectedTab: null,

        actionOptions: [],
        parsedFilterSelectConfigs: [],
        filterSearchPlaceholder: null,
        isSearching: false,
        filterResultsLabelFormatter: (rowCount) => `${rowCount} ${pluralize(`result`, rowCount)}`,
        filterRowCount: null,
        filterState: {},
        rows: [],
        rowDetailsRenderer: null,
        columns: [],
        tableAttrs: {},
        barAttrs: {},
        rowExpandState: {},
      },
      helpers: {
        stickyElemHooks,
        // Template data formatters
        getTableRows: () =>
          this.state.rows
            // TODO @evnp - roll filterSelect row filter logic into mp-table
            .filter((row) =>
              this.state.parsedFilterSelectConfigs.every((config) => {
                const chosenValues = this.selectedValuesFor(config);
                const customFilters = pick(config.customFilters || {}, chosenValues);

                const filterInUse =
                  Boolean(config.defaultFilterIsNotAll) ||
                  (chosenValues.length > 0 && chosenValues.some((value) => value !== config.defaultFilterLabel));
                const applyButtonPresent = config.applyButton;
                return (
                  applyButtonPresent ||
                  !filterInUse ||
                  (row[config.name] &&
                    chosenValues.some((value) => {
                      const propertyValues = row[config.name];
                      return propertyValues instanceof Set ? propertyValues.has(value) : propertyValues.includes(value);
                    })) ||
                  Object.entries(customFilters).some(([_, customFilter]) => customFilter(row, row[config.name]))
                );
              }),
            ),

        getTabsElRef: () => {
          const elref = this.attr(`elref`);
          if (elref) {
            return `${elref}_tabs`;
          }
          return undefined;
        },

        getTableAttrs: (): TableAttrs => {
          const {tableAttrs, columns} = this.state;
          const elref = this.attr(`elref`);
          const filterSearchText = this.getFilterSearchText();
          return {
            'default-sort-field': columns.length > 0 && columns[0].field,
            ...(filterSearchText ? {'search-filter': filterSearchText} : {}),
            ...(elref ? {elref: `${elref}_table`} : {}),
            ...tableAttrs,
          };
        },
        getBarAttrs: (): FilterBarJsxProps['attrs'] => {
          const {barAttrs, actionOptions, parsedFilterSelectConfigs, filterSearchPlaceholder} = this.state;
          return {
            elref: this.getFilterBarElRef(),
            actions: this.sortActionOptions(actionOptions),
            filters: parsedFilterSelectConfigs,
            'result-count': this.getResultsLabel(),
            'selected-count': this.getSelectedCount(),
            'search-placeholder': filterSearchPlaceholder,
            'search-text': this.getFilterSearchText(),
            'sticky-titles': this.attr(`sticky-titles`),
            ...barAttrs,
          };
        },
        // Event handlers
        handleTabChange: (ev: ToggleChangeEvent) => {
          const tabChangeEvent: TabChangeEvent = new CustomEvent(`tabChange`, {
            detail: {
              selectedTab: ev.detail.selected,
            },
          });
          this.dispatchEvent(tabChangeEvent);
        },

        handleRowDetailsVisibilityChange: (ev: RowDetailsVisibilityChangeEvent) =>
          this.dispatchEvent(new CustomEvent(`rowDetailsVisibilityChange`, {detail: ev.detail})),

        handleSelectionChange: (ev: SelectionChangeEvent) => {
          const {changed, isToggleAll} = ev.detail;
          const changedRows = Object.assign(
            {},
            ...changed.map((item) => ({
              [item.row.key]: Object.assign(item.row, {isSelected: item.isSelected}),
            })),
          );
          this.update({rows: this.state.rows.map((row) => changedRows[row.key] || row)});
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                changed: changedRows,
                selected: this.state.rows.filter((row) => row.isSelected),
                isToggleAll,
              },
            }),
          );
        },

        handleRowClick: (ev: RowClickEvent<TableRow>) =>
          this.dispatchEvent(new CustomEvent(`rowClick`, {detail: ev.detail})),

        handleCellClick: (ev: CellClickEvent<TableRow>) =>
          this.dispatchEvent(new CustomEvent(`cellClick`, {detail: ev.detail})),

        handleClickAction: (ev: SelectChangeEvent) =>
          this.dispatchEvent(new CustomEvent(`clickAction`, {detail: ev.detail})),

        handleSortChange: (ev: SortChangeEvent<TableRow>) =>
          this.dispatchEvent(new CustomEvent(`sortChange`, {detail: ev.detail})),

        handleColumnMoved: (ev: ColumnMovedEvent) =>
          this.dispatchEvent(new CustomEvent(`columnMoved`, {detail: ev.detail})),

        handleClearSelected: () => {
          this.update({rows: this.state.rows.map((row) => Object.assign(row, {isSelected: false}))});
          this.dispatchEvent(
            new CustomEvent(`selectionChange`, {
              detail: {
                selected: this.state.rows.filter((row) => row.isSelected),
                isToggleAll: true,
              },
            }),
          );
        },

        handleTableResultsChange: (ev: NumResultsChangeEvent) => {
          this.update({filterRowCount: ev.detail.numResults});
          return this.dispatchEvent(new CustomEvent(`numResultsChange`, {detail: ev.detail}));
        },

        handleFilterSearchChange: (ev: SearchChangeEvent) => {
          if (this.attr(`search-debouncing`)) {
            if (!this.state.isSearching) {
              this.update({isSearching: true});
              this.dispatchEvent(new CustomEvent(`searchStarted`));
            }

            if (this.searchChangedExecutor) {
              clearTimeout(this.searchChangedExecutor);
            }
            this.searchChangedExecutor = setTimeout(() => {
              const searchText = ev.detail.value;
              this.update({isSearching: false});
              this.updateFilterState({searchText});
              if (this.attr(`track-name`)) {
                track(TrackingEvent.TextSearch, {
                  [TrackingEventProperty.SearchQuery]: searchText,
                  [TrackingEventProperty.TrackName]: this.attr(`track-name`),
                });
              }
            }, SEARCH_INPUT_TIME_MS);
          } else {
            this.updateFilterState({searchText: ev.detail.value});
          }
        },
        handleFilterSelectChange: (ev: SelectChangeEvent) => {
          const {name, value} = ev.detail;
          this.dispatchEvent(
            new CustomEvent(`changeFilter`, {
              detail: {...ev.detail},
            }),
          );
          const config = this.state.parsedFilterSelectConfigs.find((config) => config.name === name);
          this.updateSimpleFilterSelectState(config, value);
          // RAF needed so we don't scroll before new list is rendered and end up at bottom of page
          if (this.attr(`scroll-top-on-select`)) {
            requestAnimationFrame(() => window.scrollTo(0, 0));
          }
        },
        handleFilterMenuChange: (ev: FilterMenuChangeEvent) => {
          this.dispatchEvent(
            new CustomEvent(`filterMenuChange`, {
              detail: ev.detail,
            }),
          );
        },
        handleApplyFilter: (ev: ApplyFilterEvent) => {
          this.dispatchEvent(
            new CustomEvent(`applyFilter`, {
              detail: ev.detail,
            }),
          );
        },
        filterSelectAll: (ev: FilterSelectAllEvent) => {
          const selectedConfig = this.state.parsedFilterSelectConfigs.find(
            (parsedConfig) => parsedConfig.name === ev.detail.name,
          );
          let updatedConfig;
          const {selectionState} = ev.detail;
          const previousSelected = this.getSelectedItems(selectedConfig);
          switch (selectionState) {
            case SelectionState.None:
              // we select all
              updatedConfig = this.selectAll(selectedConfig);
              break;
            case SelectionState.All:
            case SelectionState.Some:
              // we unselect all
              updatedConfig = this.unselectAll(selectedConfig);
              break;
          }
          const selected = this.getSelectedItems(updatedConfig);

          this.dispatchEvent(
            new CustomEvent(`filterSelectAll`, {
              detail: {
                name: selectedConfig.name,
                selectionState,
                previousSelected,
                selected,
              },
            }),
          );

          this.update({
            parsedFilterSelectConfigs: this.state.parsedFilterSelectConfigs.map((cfg) =>
              cfg.name === selectedConfig.name ? updatedConfig : cfg,
            ),
          });
        },
      },
    };
  }

  unselectAll(parsedFilterSelectConfig) {
    return {
      ...parsedFilterSelectConfig,
      sections: parsedFilterSelectConfig.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({...item, isSelected: false})),
      })),
    };
  }

  selectAll(parsedFilterSelectConfig) {
    return {
      ...parsedFilterSelectConfig,
      sections: parsedFilterSelectConfig.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({...item, isSelected: true})),
      })),
    };
  }

  getSelectedItems(parsedFilterSelectConfig) {
    return parsedFilterSelectConfig.sections
      .map(({items}) => items)
      .flat()
      .filter(({isSelected}) => isSelected)
      .map(({label}) => label);
  }

  set getFilterSortedMenuSections(getFilterSortedMenuSections: GetFilterSortedMenuSections) {
    this.update({getFilterSortedMenuSections});
  }

  set tabs(tabs: Array<{value: string; label: string}>) {
    this.update({tabs});
  }

  set selectedTab(selectedTab: string) {
    this.update({selectedTab});
  }

  set filterSelectConfigs(filterSelectConfigs: FilterSelectConfig[] | ParsedFilterSelectConfig[]) {
    const parsedFilterSelectConfigs: ParsedFilterSelectConfig[] = filterSelectConfigs.map((config) => {
      if (config.sections) {
        return config;
      }
      // FilterType.Simple or null
      const {options, ...otherConfigs} = config;
      const items = options.map((option) =>
        typeof option === `string` ? {label: option, isSelected: false} : {...option},
      );
      const parsedConfig: ParsedFilterSelectConfig = {
        ...otherConfigs,
        sections: [{items}],
      };

      // set default
      const {defaultFilterLabel} = parsedConfig;
      if (items.every((item) => !item.isSelected)) {
        const defaultItem = items.find((item) => item.label === defaultFilterLabel);
        if (defaultItem) {
          defaultItem.isSelected = true;
        }
      }
      return parsedConfig;
    });

    this.update({parsedFilterSelectConfigs});
  }

  set filterState(filterState: FilterState) {
    this.update({filterState});
  }

  set filterSearchPlaceholder(filterSearchPlaceholder: string) {
    this.update({filterSearchPlaceholder});
  }

  set actionOptions(actionOptions: ActionOption[]) {
    this.update({actionOptions});
  }

  set filterResultsLabelFormatter(filterResultsLabelFormatter: FilterResultsLabelFormatter) {
    this.update({filterResultsLabelFormatter});
  }

  set rows(rows: FilterBarTableRow[]) {
    this.update({rows});
  }

  set rowDetailsRenderer(val: (row: TableRow) => VNode) {
    this.update({rowDetailsRenderer: val});
  }

  set columns(columns: FilterBarTableColumn[]) {
    this.update({columns});
  }

  set tableAttrs(tableAttrs: TableAttrs) {
    this.update({tableAttrs});
  }

  set barAttrs(barAttrs: FilterBarJsxProps['attrs']) {
    this.update({barAttrs});
  }

  set customHeaderBarRenderer(customHeaderBarRenderer: () => VNode) {
    this.update({customHeaderBarRenderer});
  }

  set filterBarSelectActionRenderer(filterBarSelectActionRenderer: () => VNode) {
    this.update({filterBarSelectActionRenderer});
  }

  private getFilterState(): FilterBarState {
    const tab = this.state.selectedTab;
    return (
      (this.state.filterState && this.state.filterState[tab]) || {
        searchText: ``,
      }
    );
  }

  private getFilterSearchText() {
    return this.getFilterState().searchText;
  }

  private getFilterBarElRef() {
    const elref = this.attr(`elref`);
    if (elref) {
      return `${elref}_filterBar`;
    }
    return undefined;
  }

  private getResultsLabel() {
    const {filterRowCount, rows, filterResultsLabelFormatter} = this.state;

    if (this.attr(`total-results`)) {
      return filterResultsLabelFormatter(this.attr(`total-results`));
    }

    return filterRowCount !== null
      ? filterResultsLabelFormatter(filterRowCount)
      : filterResultsLabelFormatter(rows.length);
  }

  private getSelectedCount(): number {
    return this.state.rows.filter((row) => row.isSelected).length;
  }

  private selectedValuesFor(config: ParsedFilterSelectConfig): string[] {
    return config.sections
      .map(({items}) => items)
      .flat()
      .filter((item) => item.isSelected)
      .map((item) => item.label);
  }

  private sortActionOptions(actionOptions: ActionOption[]): ActionOption[] {
    return actionOptions.sort((a, b) => {
      const left = a.orderPriority || actionOptions.length;
      const right = b.orderPriority || actionOptions.length;
      return left - right;
    });
  }

  private updateFilterState(newState: FilterBarState) {
    const tab = this.state.selectedTab;
    const filterState = {
      ...(this.state.filterState || {}),
      [tab]: {
        ...this.getFilterState(),
        ...newState,
      },
    };
    this.update({filterState});

    this.dispatchEvent(
      new CustomEvent(`filterStateChange`, {
        detail: cloneDeep(filterState),
      }),
    );
  }

  private updateSimpleFilterSelectState(config: ParsedFilterSimpleConfig, value: string) {
    const {multiple} = config;
    let {sections, name} = config;
    if (multiple && value !== config.defaultFilterLabel) {
      //toggle value, and set defaultFilterLabel false
      sections = sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          isSelected:
            item.label !== config.defaultFilterLabel && (item.label === value ? !item.isSelected : item.isSelected),
        })),
      }));
    } else {
      //set value
      sections = sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({...item, isSelected: item.label === value})),
      }));
    }

    this.update({
      parsedFilterSelectConfigs: this.state.parsedFilterSelectConfigs.map((cfg) =>
        cfg.name === name ? {...cfg, sections} : cfg,
      ),
    });

    // TODO(gonzalo): this event shouldn't be necessary. The only possible update is when we select an option, and we already expose the changeFilter event
    this.dispatchEvent(
      new CustomEvent(`updateParsedFilterSelectConfigs`, {
        detail: {parsedFilterSelectConfigs: this.state.parsedFilterSelectConfigs},
      }),
    );
  }
}

export const FilterBarTableEl = defineAndMakeJsxFactory<FilterTableJsxProps>(`mp-filter-bar-table`, FilterBarTable);
export const FilterBarTableReact = panel2react<FilterTableJsxProps>(`mp-filter-bar-table`, FilterBarTable);
